import { create } from "zustand";

/**
 * The four disclosure panels under the app header (settings, tools, history,
 * session) share one open slot: opening any one closes whichever else was
 * open, so at most one panel is ever expanded at a time. Toggling the panel
 * that is already open closes it, leaving nothing open.
 *
 * SettingsBar, ToolsDrawer and HistoryPanel each keep their own thin wrapper
 * over this store (useSettingsPanel, useHistoryPanel, the tools drawer's own
 * hook) so their call sites and exported test hooks read the same as before;
 * this store is the single source of truth underneath all three.
 */
export type Panel = "settings" | "tools" | "history" | "session";

export const usePanelStore = create<{
  open: Panel | null;
  toggle: (panel: Panel) => void;
  close: (panel?: Panel) => void;
}>((set) => ({
  open: null,
  toggle: (panel) => set((s) => ({ open: s.open === panel ? null : panel })),
  // With no argument, closes whichever panel is open. With one, closes only
  // if that panel is the one currently open (a no-op otherwise).
  close: (panel) =>
    set((s) => (panel === undefined || s.open === panel ? { open: null } : s)),
}));
